import type { Product } from '@/types/mode-list-products';

import useLocale from '@/composables/useLocale';
import { formatLocalDate, formatStartDate } from '@/utils/utils';

class PublicApi {
  private readonly apiURL;
  private readonly apiKey;
  private distributorId: any;
  activityDetails: any;

  constructor(apiURL: string, apiKey: string) {
    this.apiURL = apiURL;
    this.apiKey = apiKey;
  }

  async fetchDistributor() {
    const url = `${this.apiURL}/v3/distributors/me`;
    const options = {
      method: 'GET',
      headers: { Accept: 'application/json', Authorization: `Bearer ${this.apiKey}` },
    };

    let me = null;
    try {
      const response = await fetch(url, options);
      me = await response.json();
    }
    catch (err) {
      console.error('Error while loading user', err);
    }

    if (me?.error) {
      throw new Error(me);
    }

    this.distributorId = me?.user.distributorId ?? null;
  }

  async fetchActivityDetails(id: string | number) {
    const url = `${this.apiURL}/v3/products/${id}`;
    const options = {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${this.apiKey}` },
      body: JSON.stringify({
        properties: [{
          type: 'supplier',
        }, {
          type: 'rating',
        }, {
          type: 'nextSessionDate',
        }, {
          type: 'product_enrichment_by_distributor',
          distributorId: this.distributorId,
        }],
      }),
    };

    let product = null;
    try {
      const response = await fetch(url, options);
      product = await response.json();
    }
    catch (err) {
      console.error('Error while loading activity', err);
    }

    if (product?.error) {
      throw new Error(product);
    }

    this.activityDetails = product?.data ?? null;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  async getNextSession(id: string | number, signal: AbortSignal) {
    if (this.activityDetails === null)
      return;
    return this.activityDetails.properties?.find((prop: any) => prop.propertyName === 'nextSessionDate')?.value;
  }

  getSessionFromProduct(product: any) {
    return product.properties?.find((prop: any) => prop.propertyName === 'nextSessionDate')?.value;
  }

  formatSession(session: any) {
    const { currentLocale } = useLocale();
    const availabilityType = session?.availabilityType;
    if (availabilityType && availabilityType === 'OPENING_HOURS') {
      return formatLocalDate(session.date);
    }
    else if (availabilityType && availabilityType === 'START_TIME') {
      return formatStartDate(session.date);
    }
    else {
      return currentLocale.value === 'fr-FR' ? 'Disponible bientôt' : 'Available soon';
    }
  }

  calculateDuration(session: any) {
    return session?.duration ? Math.floor(session.duration / (1000 * 60)) : 0;
  }

  async fetchCrossSellingActivities(ids: string[], signal: AbortSignal) {
    const { currentLocale } = useLocale();
    const url = `${this.apiURL}/v3/products/list`;
    const today = new Date();
    const nextMonth = new Date();
    nextMonth.setMonth(today.getMonth() + 1);
    // const gps = this.activityDetails.meetingPoint?.location?.gps ?? {lon:-1.2, lat:46.16}
    // todo : ajout du geo_dist ou récupérer les offres définies en crosssell dans le BO
    // geo_dist:{lat:gps.lat ,lon:gps.lon ,distance: '50km'}
    const bodyV3 = ids.length > 0
      ? {
          size: ids.length,
          sorts: [{ type: 'next_session' }],
          filters: [{
            filterProperties: {
              type: 'productIds',
            },
            selection: [...ids],
          }, {
            filterProperties: {
              type: 'custom_filter',
              target: 'product',
              field: 'imported_in_langs',
            },
            selection: currentLocale.value,
          }],
          properties: [{ type: 'supplier' }, { type: 'nextSessionDate' }, { type: 'rating' }],
        }
      : {
          size: 8,
          sorts: [{ type: 'next_session' }],
          filters: [{
            filterProperties: {
              type: 'custom_filter',
              target: 'product_enrichment',
              field: 'distributor_id',
            },
            selection: this.distributorId,
          }, {
            filterProperties: {
              type: 'date_range',
            },
            selection: {
              startDate: today.getTime(),
              endDate: nextMonth.getTime(),
            },
          }, {
            filterProperties: {
              type: 'custom_filter',
              target: 'product',
              field: 'imported_in_langs',
            },
            selection: currentLocale.value,
          }],
          properties: [{ type: 'supplier' }, { type: 'nextSessionDate' }, { type: 'rating' }],
        };

    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(bodyV3),
      signal,
    };

    let crossSellingActivities: Product[] = [];
    try {
      const response = await fetch(url, options);
      const products = await response.json();
      crossSellingActivities = [...products.data];
    }
    catch (err) {
      console.error('Error while loading cross sellings', err);
    }
    return crossSellingActivities;
  }

  /**
   * @return {YoubookProduct | null}
   */
  getActivityDetails() {
    return this.activityDetails;
  }

  getActivityDescriptions(locale: string) {
    return this.activityDetails.descriptions?.filter((it: any) => it.lang === locale.toString());
  }

  getSupplierName() {
    return this.activityDetails.properties?.find((it: any) => it.propertyName === 'supplier')?.value.name;
  }

  getFairguestWidgetId() {
    // todo : endpoint pour récupérer cette info
    return '1478';
  }

  getSitExternalId() {
    return this.activityDetails.properties?.find((prop: any) => prop.propertyName === 'productEnrichmentByDistributor')
      .value
      ?.external_references
      ?.filter((ref: any) => ref.type === 'SIT')
      .map((ref: any) => ref.value)[0];
  }

  getProviderWidgetUrl(locale: string) {
    const url = `${this.apiURL}/static/widgets?apiKey=${this.apiKey}&productId=${this.activityDetails.id}&locale=${locale}`;
    let activityParams = '';
    switch (this.activityDetails.source) {
      case 'guidap':
        // url = `${this.#apiURL}/widgets/guidap?client_token=TFXVkglavR8oDeinb13QBpxLGmqOZ4HUPS9C&language=fr`
        activityParams = `&g-path=/cart/add-to-cart/${this.activityDetails.sourceId}/undefined/DEPARTURE`;
        break;
      default:
        break;
    }
    return url + activityParams;
  }
}

export { PublicApi };
